/**
 * Selectors for the Detailmasken (customer spec of 2026-09-22): the seven
 * project pages Übersicht · Execution · Readiness · Quality · Dependencies ·
 * Trends · Actions.
 *
 * Like `intelligence.ts` these are pure functions over already-fetched data.
 * They never judge anything the engine has not: lateness, readiness gaps and
 * execution states come from the backend (KPIs and the findings endpoint), so
 * a list here cannot call something late that the score beside it does not
 * count. What is decided here is only ranking and presentation.
 */
import type { TFunction } from 'i18next';
import {
  ActionAlertDto,
  ActionArea,
  ActionClosedReason,
  ActionPointDto,
  ActionPriority,
  ActionSource,
  ActionSeverity,
  CheckpointFindingDto,
  DeliverObjectCriticality,
  DeliverableFindingDto,
  DependencyEdgeDto,
  DependencyGraphDto,
  KpiCategory,
  KpiHistoryDto,
  KpiScope,
  KpiValueDto,
  ReadinessGap,
  Status,
} from '../../api/model';
import {
  HistoryPoint,
  KPI,
  KpiIndex,
  categorical,
  categoricalByScopeId,
  numeric,
  numericByScopeId,
} from './intelligence';
import { getProjectHealthStatus, ProjectHealthKey } from './reportData';
import { chrome, status as statusColors } from './reportTheme';

// ---------------------------------------------------------------------------
// Tones: the one colour scale every tile, badge and bar uses
// ---------------------------------------------------------------------------

export type Tone = 'good' | 'warning' | 'serious' | 'critical' | 'neutral';

export const TONE_COLOR: Record<Tone, string> = {
  good: statusColors.good,
  warning: statusColors.warning,
  serious: statusColors.serious,
  critical: statusColors.critical,
  neutral: chrome.muted,
};

const TONE_BY_HEALTH_KEY: Record<ProjectHealthKey, Tone> = {
  onTrack: 'good',
  watch: 'warning',
  atRisk: 'serious',
  critical: 'critical',
};

/**
 * A 0-100 score's band - the same four bands as ph.project_health_status
 * (spec §12), applied to every score so one colour means one thing everywhere.
 */
export function scoreBand(value: number | undefined): { key: ProjectHealthKey; tone: Tone } | undefined {
  if (typeof value !== 'number') return undefined;
  const key = getProjectHealthStatus(value).key;
  return { key, tone: TONE_BY_HEALTH_KEY[key] };
}

/** Rates where lower is better (overdue, delayed, violated): the band of the complement. */
export function inverseRateTone(value: number | undefined): Tone | undefined {
  return typeof value === 'number' ? scoreBand(100 - value)?.tone : undefined;
}

// ---------------------------------------------------------------------------
// Drill-down to the object itself (the last step of the spec's chain)
// ---------------------------------------------------------------------------

export type ObjectTarget =
  | { kind: 'task'; id: number }
  | { kind: 'deliverable'; id: number }
  | { kind: 'checkpoint'; id: number }
  | { kind: 'actionPoint'; id: number };

/** The project page's search params that name the object whose detail is open. */
export interface ObjectSearch {
  taskId?: number;
  deliverableId?: number;
  checkpointId?: number;
  actionPointId?: number;
}

/**
 * The search params that open an object's detail dialog over the current page
 * of the project. The page itself stays: no section is set, so closing the
 * dialog returns to where the link was clicked. Every id is named, so opening
 * one object replaces whichever was open before.
 */
export function objectSearch(target?: ObjectTarget): ObjectSearch {
  return {
    taskId: target?.kind === 'task' ? target.id : undefined,
    deliverableId: target?.kind === 'deliverable' ? target.id : undefined,
    checkpointId: target?.kind === 'checkpoint' ? target.id : undefined,
    actionPointId: target?.kind === 'actionPoint' ? target.id : undefined,
  };
}

/** The object a project URL opens, if any. */
export function objectFromSearch(search: ObjectSearch): ObjectTarget | undefined {
  if (search.taskId) return { kind: 'task', id: search.taskId };
  if (search.deliverableId) return { kind: 'deliverable', id: search.deliverableId };
  if (search.checkpointId) return { kind: 'checkpoint', id: search.checkpointId };
  if (search.actionPointId) return { kind: 'actionPoint', id: search.actionPointId };
  return undefined;
}

// ---------------------------------------------------------------------------
// Areas: the four intelligence dimensions an action or finding belongs to
// ---------------------------------------------------------------------------

export type Area = 'execution' | 'readiness' | 'quality' | 'dependencies';

export const AREAS: Area[] = ['execution', 'readiness', 'quality', 'dependencies'];

const AREA_BY_CATEGORY: Partial<Record<string, Area>> = {
  [KpiCategory.EXECUTION]: 'execution',
  [KpiCategory.READINESS]: 'readiness',
  [KpiCategory.QUALITY_ASSURANCE]: 'quality',
  [KpiCategory.DEPENDENCY_HEALTH]: 'dependencies',
};

export function areaOf(alert: ActionAlertDto): Area | undefined {
  return alert.category ? AREA_BY_CATEGORY[alert.category] : undefined;
}

export function alertsInArea(alerts: ActionAlertDto[] | undefined, area: Area): ActionAlertDto[] {
  return (alerts ?? []).filter((alert) => areaOf(alert) === area);
}

const SEVERITY_RANK: Record<string, number> = {
  [ActionSeverity.CRITICAL]: 0,
  [ActionSeverity.WARNING]: 1,
  [ActionSeverity.INFO]: 2,
};

export function bySeverity(a: ActionAlertDto, b: ActionAlertDto): number {
  return (SEVERITY_RANK[a.severity ?? ''] ?? 9) - (SEVERITY_RANK[b.severity ?? ''] ?? 9);
}

export const SEVERITY_TONE: Record<string, Tone> = {
  [ActionSeverity.CRITICAL]: 'critical',
  [ActionSeverity.WARNING]: 'warning',
  [ActionSeverity.INFO]: 'neutral',
};

// ---------------------------------------------------------------------------
// History: deltas "vs. 4 weeks ago" and multi-series charts
// ---------------------------------------------------------------------------

/** The keys whose history the report loads in one call. */
export const HISTORY_KEYS = [
  KPI.projectHealthScore,
  KPI.readinessScore,
  KPI.executionScore,
  KPI.qualityScore,
  KPI.dependencyScore,
  KPI.projectProgress,
  KPI.qualityAssuredProjectProgress,
  KPI.qaCoverage,
  KPI.projectQaCompletion,
  KPI.qaTimeliness,
  KPI.planningReadinessLo,
  KPI.ownershipReadiness,
  KPI.structureReadiness,
  KPI.qualityReadiness,
  KPI.scheduleConsistency,
  KPI.projectPhaseDataReadiness,
  KPI.projectLoDataReadiness,
  KPI.overdueDeliverableRate,
  KPI.overduePhaseRate,
  KPI.scheduleReliability,
  KPI.criticalDeliveryPerformance,
  KPI.projectProgressPerformance,
  KPI.delayedCheckpointRate,
  KPI.hardDependencyViolationRate,
  KPI.overduePredecessorRate,
  KPI.parallelDependencyRiskRate,
  KPI.dependencyIntegrity,
  // phase-scoped: the three phase trends TR-01..03
  KPI.phaseProgress,
  KPI.phaseQaCompletion,
  KPI.phaseStatus,
] as const;

/** Days of history the report loads - the spec's "letzte 12 Monate". */
export const HISTORY_WINDOW_DAYS = 365;

export type HistoryIndex = Map<string, HistoryPoint[]>;

/** Project-scope history by KPI key. Days without a value have no point. */
export function indexHistory(history: KpiHistoryDto[] | undefined): HistoryIndex {
  const byKey: HistoryIndex = new Map();
  for (const series of history ?? []) {
    if (series.scopeType !== KpiScope.PROJECT || !series.key) continue;
    byKey.set(
      series.key,
      (series.points ?? [])
        .filter((p) => typeof p.numericValue === 'number' && p.date)
        .map((p) => ({ date: p.date as string, value: p.numericValue as number })),
    );
  }
  return byKey;
}

export interface CategoricalPoint {
  date: string;
  value: string;
}

/** A scope-instance history, e.g. per phase, keyed by scope id. */
export function scopedHistory(
  history: KpiHistoryDto[] | undefined,
  key: string,
  scope: KpiScope,
): Map<number, { numeric: HistoryPoint[]; categorical: CategoricalPoint[] }> {
  const byScope = new Map<number, { numeric: HistoryPoint[]; categorical: CategoricalPoint[] }>();
  for (const series of history ?? []) {
    if (series.key !== key || series.scopeType !== scope || series.scopeId == null) continue;
    const points = series.points ?? [];
    byScope.set(series.scopeId, {
      numeric: points
        .filter((p) => typeof p.numericValue === 'number' && p.date)
        .map((p) => ({ date: p.date as string, value: p.numericValue as number })),
      categorical: points
        .filter((p) => p.categoricalValue && p.date)
        .map((p) => ({ date: p.date as string, value: p.categoricalValue as string })),
    });
  }
  return byScope;
}

export const isoDay = (date: Date): string => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

export const isoDaysAgo = (days: number, from: Date = new Date()): string =>
  isoDay(new Date(from.getTime() - days * 86_400_000));

export interface Change {
  delta: number;
  /** The day compared against. */
  since: string;
  /** True when history does not reach back the full window yet. */
  partial: boolean;
}

/**
 * The change of a KPI's current value against its value `days` ago - the
 * "vs. letzte 4 Wochen" under every tile.
 *
 * History only starts on the first recompute after a KPI began to be tracked,
 * so a young project has no point that old. Rather than show nothing for its
 * first month, the change is then taken against the oldest day there is and
 * flagged `partial`, so the tile can say "since 12.09." instead of implying
 * four weeks.
 */
export function changeOver(
  points: HistoryPoint[] | undefined,
  current: number | undefined,
  days: number,
  today: Date = new Date(),
): Change | undefined {
  if (typeof current !== 'number' || !points?.length) return undefined;
  const target = isoDaysAgo(days, today);
  const todayIso = isoDay(today);

  let base: HistoryPoint | undefined;
  for (const point of points) {
    if (point.date <= target) base = point;
  }
  const partial = base === undefined;
  if (!base) {
    base = points[0];
    if (base.date >= todayIso) return undefined; // only today's value: nothing to compare with
  }
  return { delta: current - base.value, since: base.date, partial };
}

export type Direction = 'up' | 'down' | 'flat';

export function directionOf(delta: number | undefined): Direction {
  if (typeof delta !== 'number') return 'flat';
  const rounded = Math.round(delta);
  return rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
}

export function signed(delta: number, unit = ''): string {
  const rounded = Math.round(delta);
  if (rounded === 0) return `±0${unit}`;
  return `${rounded > 0 ? '+' : ''}${rounded}${unit}`;
}

/** Only the points inside the last `days` days. */
export function withinDays(points: HistoryPoint[] | undefined, days: number, today = new Date()): HistoryPoint[] {
  const from = isoDaysAgo(days, today);
  return (points ?? []).filter((p) => p.date >= from);
}

// ---------------------------------------------------------------------------
// Phases
// ---------------------------------------------------------------------------

/** ON_HOLD: paused by decision (Phase.lifecycle) - phases only. */
export type ExecutionState = 'ON_PLAN' | 'BEHIND' | 'CRITICAL' | 'NOT_STARTED' | 'FINISHED' | 'ON_HOLD';

export const EXECUTION_TONE: Record<ExecutionState, Tone> = {
  ON_PLAN: 'good',
  FINISHED: 'good',
  BEHIND: 'warning',
  CRITICAL: 'critical',
  NOT_STARTED: 'neutral',
  ON_HOLD: 'neutral',
};

export interface PhaseExecutionRow {
  id: number;
  name: string;
  actual?: number;
  expected?: number;
  variance?: number;
  status?: ExecutionState;
}

/**
 * One row per phase, in the project's phase order. "Actual" is the weighted
 * phase progress because that is what the variance is computed from - showing
 * the unweighted one would make actual minus expected disagree with the
 * variance printed beside it.
 */
export function phaseExecutionRows(
  values: KpiValueDto[] | undefined,
  phaseNames: Map<number, string>,
): PhaseExecutionRow[] {
  const actual = numericByScopeId(values, KPI.phaseProgressWeighted, KpiScope.PHASE);
  const expected = numericByScopeId(values, KPI.expectedPhaseProgress, KpiScope.PHASE);
  const variance = numericByScopeId(values, KPI.phaseProgressVariance, KpiScope.PHASE);
  const status = categoricalByScopeId(values, KPI.phaseExecutionStatus, KpiScope.PHASE);
  return [...phaseNames.entries()].map(([id, name]) => ({
    id,
    name,
    actual: actual.get(id),
    expected: expected.get(id),
    variance: variance.get(id),
    status: status.get(id) as ExecutionState | undefined,
  }));
}

export interface ExecutionSplit {
  onPlan: number;
  behind: number;
  critical: number;
  total: number;
}

/**
 * The Execution page's three tiles. A finished phase and one not yet due to
 * start are not behind, so they count as on plan; a paused phase is neither on
 * nor off plan, and like a phase with no status (no tasks, no dates) it is left
 * out of all three and of the total.
 */
export function executionSplit(rows: PhaseExecutionRow[]): ExecutionSplit {
  const split = { onPlan: 0, behind: 0, critical: 0, total: 0 };
  for (const row of rows) {
    if (!row.status || row.status === 'ON_HOLD') continue;
    split.total++;
    if (row.status === 'BEHIND') split.behind++;
    else if (row.status === 'CRITICAL') split.critical++;
    else split.onPlan++;
  }
  return split;
}

// ---------------------------------------------------------------------------
// Deliverables
// ---------------------------------------------------------------------------

const CRITICALITY_RANK: Record<string, number> = {
  [DeliverObjectCriticality.HIGH]: 0,
  [DeliverObjectCriticality.MIDDLE]: 1,
  [DeliverObjectCriticality.EASY]: 2,
};

const criticalityRank = (value?: string | null) => CRITICALITY_RANK[value ?? ''] ?? 3;

export const isHigh = (value?: string | null) => value === DeliverObjectCriticality.HIGH;

export interface DeliverableRow extends DeliverableFindingDto {
  phaseName: string;
  expected?: number;
  variance?: number;
  status?: ExecutionState;
  dataReadiness?: number;
}

export function deliverableRows(
  findings: DeliverableFindingDto[] | undefined,
  values: KpiValueDto[] | undefined,
  phaseNames: Map<number, string>,
): DeliverableRow[] {
  const expected = numericByScopeId(values, KPI.expectedLoProgress, KpiScope.DELIVER_OBJECT);
  const variance = numericByScopeId(values, KPI.loProgressVariance, KpiScope.DELIVER_OBJECT);
  const status = categoricalByScopeId(values, KPI.loExecutionStatus, KpiScope.DELIVER_OBJECT);
  const readiness = numericByScopeId(values, KPI.loDataReadiness, KpiScope.DELIVER_OBJECT);
  return (findings ?? [])
    .filter((f): f is DeliverableFindingDto & { id: number } => f.id != null)
    .map((f) => ({
      ...f,
      phaseName: (f.phaseId != null ? phaseNames.get(f.phaseId) : undefined) ?? '—',
      expected: expected.get(f.id),
      variance: variance.get(f.id),
      status: status.get(f.id) as ExecutionState | undefined,
      dataReadiness: readiness.get(f.id),
    }));
}

/**
 * The Execution page's "Auffällige Lieferobjekte": overdue ones first (longest
 * overdue first), then critically and then merely behind plan; high
 * criticality breaks ties. Deliverables that are on plan, finished or not yet
 * due are not "auffällig" and are left out rather than padding the list.
 */
export function conspicuousDeliverables(rows: DeliverableRow[]): DeliverableRow[] {
  const urgency = (row: DeliverableRow) =>
    row.daysOverdue != null ? 0 : row.status === 'CRITICAL' ? 1 : row.status === 'BEHIND' ? 2 : 9;
  return rows
    .filter((row) => urgency(row) < 9)
    .sort(
      (a, b) =>
        urgency(a) - urgency(b) ||
        (b.daysOverdue ?? 0) - (a.daysOverdue ?? 0) ||
        criticalityRank(a.criticality) - criticalityRank(b.criticality) ||
        (a.variance ?? 0) - (b.variance ?? 0),
    );
}

/** Readiness-gap order, most fundamental first - also the order "main reason" picks from. */
export const GAP_ORDER: ReadinessGap[] = [
  ReadinessGap.NO_TASKS,
  ReadinessGap.UNASSIGNED_TASKS,
  ReadinessGap.MISSING_DATES,
  ReadinessGap.NO_CHECKPOINTS,
  ReadinessGap.OUTSIDE_PHASE,
  ReadinessGap.MISSING_CRITICALITY,
];

export function mainGap(row: DeliverableFindingDto): ReadinessGap | undefined {
  return GAP_ORDER.find((gap) => row.readinessGaps?.includes(gap));
}

/** Deliverables with the most gaps first, high criticality breaking ties. */
export function byReadinessGaps(rows: DeliverableRow[]): DeliverableRow[] {
  return [...rows].sort(
    (a, b) =>
      (b.readinessGaps?.length ?? 0) - (a.readinessGaps?.length ?? 0) ||
      criticalityRank(a.criticality) - criticalityRank(b.criticality) ||
      (a.name ?? '').localeCompare(b.name ?? ''),
  );
}

export interface ReadinessPoint {
  gap: ReadinessGap;
  count: number;
  highCount: number;
}

/**
 * "Offene Readiness-Punkte": one point per kind of gap, with how many
 * deliverables have it and how many of those are high-criticality. Ranked by
 * the high-criticality ones first - the spec's "Relevanz/Kritikalität der
 * betroffenen Objekte" - then by how many are affected.
 */
export function readinessPoints(rows: DeliverableFindingDto[] | undefined): ReadinessPoint[] {
  return GAP_ORDER.map((gap) => {
    const affected = (rows ?? []).filter((row) => row.readinessGaps?.includes(gap));
    return { gap, count: affected.length, highCount: affected.filter((r) => isHigh(r.criticality)).length };
  })
    .filter((point) => point.count > 0)
    .sort((a, b) => b.highCount - a.highCount || b.count - a.count);
}

// ---------------------------------------------------------------------------
// Checkpoints
// ---------------------------------------------------------------------------

export interface CheckpointRow extends CheckpointFindingDto {
  deliverableName: string;
  phaseName: string;
  criticality?: string;
}

/** Open checkpoints, delayed first, then those of high-criticality deliverables, then by due date. */
export function checkpointRows(
  checkpoints: CheckpointFindingDto[] | undefined,
  deliverables: DeliverableFindingDto[] | undefined,
  phaseNames: Map<number, string>,
): CheckpointRow[] {
  const byId = new Map((deliverables ?? []).map((d) => [d.id, d]));
  return (checkpoints ?? [])
    .map((cp) => {
      const deliverable = cp.deliverableId != null ? byId.get(cp.deliverableId) : undefined;
      return {
        ...cp,
        deliverableName: deliverable?.name ?? '—',
        phaseName: (cp.phaseId != null ? phaseNames.get(cp.phaseId) : undefined) ?? '—',
        criticality: deliverable?.criticality ?? undefined,
      };
    })
    .sort(
      (a, b) =>
        Number(b.delayed) - Number(a.delayed) ||
        criticalityRank(a.criticality) - criticalityRank(b.criticality) ||
        (a.validationDate ?? '9999').localeCompare(b.validationDate ?? '9999'),
    );
}

// ---------------------------------------------------------------------------
// Dependencies
// ---------------------------------------------------------------------------

export type DependencyState = 'VIOLATED' | 'PREDECESSOR_LATE' | 'OK';

export const DEPENDENCY_TONE: Record<DependencyState, Tone> = {
  VIOLATED: 'critical',
  PREDECESSOR_LATE: 'warning',
  OK: 'good',
};

export function dependencyState(edge: DependencyEdgeDto): DependencyState {
  if (edge.violated) return 'VIOLATED';
  if (edge.predecessorDaysOverdue != null) return 'PREDECESSOR_LATE';
  return 'OK';
}

export interface DependencyRow {
  id: number;
  edge: DependencyEdgeDto;
  predecessor: string;
  successor: string;
  successorPhaseId?: number;
  successorCriticality?: string;
  state: DependencyState;
}

export function dependencyRows(graph: DependencyGraphDto | undefined): DependencyRow[] {
  const nodes = new Map((graph?.nodes ?? []).map((n) => [n.id, n]));
  return (graph?.edges ?? [])
    .filter((e): e is DependencyEdgeDto & { id: number } => e.id != null)
    .map((edge) => {
      const successor = edge.to != null ? nodes.get(edge.to) : undefined;
      return {
        id: edge.id,
        edge,
        predecessor: (edge.from != null ? nodes.get(edge.from)?.label : undefined) ?? `#${edge.from}`,
        successor: successor?.label ?? `#${edge.to}`,
        successorPhaseId: successor?.phaseId ?? undefined,
        successorCriticality: successor?.criticality ?? undefined,
        state: dependencyState(edge),
      };
    });
}

const STATE_RANK: Record<DependencyState, number> = { VIOLATED: 0, PREDECESSOR_LATE: 1, OK: 2 };

/** Violations first, then late predecessors - longest delay and high criticality first. */
export function conspicuousDependencies(rows: DependencyRow[]): DependencyRow[] {
  return rows
    .filter((row) => row.state !== 'OK')
    .sort(
      (a, b) =>
        STATE_RANK[a.state] - STATE_RANK[b.state] ||
        (b.edge.predecessorDaysOverdue ?? 0) - (a.edge.predecessorDaysOverdue ?? 0) ||
        criticalityRank(a.successorCriticality) - criticalityRank(b.successorCriticality),
    );
}

export interface DependencyPhaseRow {
  id: number;
  name: string;
  total: number;
  violated: number;
  late: number;
  ok: number;
}

/**
 * Dependencies per phase, attributed to the successor's phase: that is where a
 * late or violated predecessor lands, which is what "where is dependency
 * action needed" asks.
 */
export function dependenciesByPhase(rows: DependencyRow[], phaseNames: Map<number, string>): DependencyPhaseRow[] {
  return [...phaseNames.entries()].map(([id, name]) => {
    const inPhase = rows.filter((row) => row.successorPhaseId === id);
    return {
      id,
      name,
      total: inPhase.length,
      violated: inPhase.filter((r) => r.state === 'VIOLATED').length,
      late: inPhase.filter((r) => r.state === 'PREDECESSOR_LATE').length,
      ok: inPhase.filter((r) => r.state === 'OK').length,
    };
  });
}

// ---------------------------------------------------------------------------
// Übersicht: main drivers
// ---------------------------------------------------------------------------

export type DriverKind =
  | 'deliverableOverdue'
  | 'deliverableCritical'
  | 'checkpointsDelayed'
  | 'dependencyViolated'
  | 'tasksUnassigned'
  | 'progressBelowPlan';

export interface Driver {
  id: string;
  kind: DriverKind;
  area: Area;
  tone: Tone;
  /** The object the driver is about, when it is about one. */
  target?: ObjectTarget;
  /** Interpolation values for the theme and impact texts. */
  params: Record<string, string | number>;
}

/**
 * "Haupttreiber / Handlungsfelder": the concrete facts behind the project's
 * state, one per affected object where the object is what matters (a late
 * deliverable, a violated dependency) and aggregated where the count is (open
 * checkpoints, unassigned tasks).
 *
 * The spec's own examples, all derived from findings the engine already judged.
 * Critical when a high-criticality deliverable is involved or the engine says
 * critical; the spec is explicit these are not risk assessments, so nothing
 * here is weighted into a score.
 */
export function mainDrivers(
  deliverables: DeliverableRow[],
  checkpoints: CheckpointRow[],
  dependencies: DependencyRow[],
  index: KpiIndex,
  limit = 5,
): Driver[] {
  const drivers: Driver[] = [];

  for (const row of conspicuousDeliverables(deliverables)) {
    if (row.daysOverdue != null) {
      drivers.push({
        id: `lo-overdue-${row.id}`,
        kind: 'deliverableOverdue',
        area: 'execution',
        tone: isHigh(row.criticality) ? 'critical' : 'warning',
        target: { kind: 'deliverable', id: row.id as number },
        params: { name: row.name ?? `#${row.id}`, days: row.daysOverdue },
      });
    } else if (row.status === 'CRITICAL') {
      drivers.push({
        id: `lo-critical-${row.id}`,
        kind: 'deliverableCritical',
        area: 'execution',
        tone: isHigh(row.criticality) ? 'critical' : 'warning',
        target: { kind: 'deliverable', id: row.id as number },
        params: { name: row.name ?? `#${row.id}`, pp: Math.round(row.variance ?? 0) },
      });
    }
  }

  const delayed = checkpoints.filter((cp) => cp.delayed);
  if (delayed.length > 0) {
    drivers.push({
      id: 'checkpoints-delayed',
      kind: 'checkpointsDelayed',
      area: 'quality',
      tone: delayed.some((cp) => isHigh(cp.criticality)) ? 'critical' : 'warning',
      params: { count: delayed.length },
    });
  }

  for (const row of dependencies.filter((d) => d.state === 'VIOLATED')) {
    drivers.push({
      id: `dep-${row.id}`,
      kind: 'dependencyViolated',
      area: 'dependencies',
      tone: 'critical',
      // the successor is what the violation holds up
      target: row.edge.to != null ? { kind: 'deliverable', id: row.edge.to } : undefined,
      params: {
        from: row.predecessor,
        to: row.successor,
        days: row.edge.predecessorDaysOverdue ?? 0,
      },
    });
  }

  const unassigned = deliverables.reduce((sum, d) => sum + (d.unassignedTaskCount ?? 0), 0);
  if (unassigned > 0) {
    drivers.push({
      id: 'tasks-unassigned',
      kind: 'tasksUnassigned',
      area: 'readiness',
      tone: 'warning',
      params: { count: unassigned },
    });
  }

  const projectStatus = categorical(index, KPI.projectExecutionStatus);
  if (projectStatus === 'BEHIND' || projectStatus === 'CRITICAL') {
    drivers.push({
      id: 'progress-below-plan',
      kind: 'progressBelowPlan',
      area: 'execution',
      tone: projectStatus === 'CRITICAL' ? 'critical' : 'warning',
      params: {
        actual: Math.round(numeric(index, KPI.projectProgress) ?? 0),
        expected: Math.round(numeric(index, KPI.expectedProjectProgress) ?? 0),
      },
    });
  }

  const toneRank: Record<Tone, number> = { critical: 0, serious: 1, warning: 2, neutral: 3, good: 4 };
  // stable sort: within a tone, the order above (execution facts, QA, dependencies, readiness)
  return drivers.sort((a, b) => toneRank[a.tone] - toneRank[b.tone]).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Action Points (the existing, manually managed ones)
// ---------------------------------------------------------------------------

export type ActionState = 'open' | 'inProgress' | 'overdue' | 'done' | 'cancelled';

const isClosed = (point: ActionPointDto) =>
  point.status === Status.FINISHED || point.status === Status.CANCELLED;

export function isOverdue(point: ActionPointDto, today = isoDay(new Date())): boolean {
  return !isClosed(point) && !!point.validationDate && point.validationDate < today;
}

export function actionState(point: ActionPointDto, today = isoDay(new Date())): ActionState {
  if (point.status === Status.FINISHED) return 'done';
  if (point.status === Status.CANCELLED) return 'cancelled';
  if (isOverdue(point, today)) return 'overdue';
  if (point.status === Status.IN_PROGRESS || point.status === Status.DELAYED) return 'inProgress';
  return 'open';
}

export const ACTION_STATE_TONE: Record<ActionState, Tone> = {
  open: 'neutral',
  inProgress: 'warning',
  overdue: 'critical',
  done: 'good',
  cancelled: 'neutral',
};

export interface ActionSummary {
  open: number;
  overdue: number;
  dueSoon: number;
  done: number;
  /** Of the done ones, closed automatically because their rule no longer applied. */
  autoClosed: number;
  unassigned: number;
  /** Done / (everything not cancelled), or undefined when there is nothing to do. */
  completionRate?: number;
}

/** The Actions page's tiles, from the project's Action Points. */
export function actionSummary(points: ActionPointDto[] | undefined, today = new Date()): ActionSummary {
  const todayIso = isoDay(today);
  const in30 = isoDay(new Date(today.getTime() + 30 * 86_400_000));
  const relevant = (points ?? []).filter((p) => p.status !== Status.CANCELLED);
  const open = relevant.filter((p) => !isClosed(p));
  const done = relevant.filter((p) => p.status === Status.FINISHED).length;
  return {
    open: open.length,
    overdue: open.filter((p) => isOverdue(p, todayIso)).length,
    dueSoon: open.filter((p) => !!p.validationDate && p.validationDate >= todayIso && p.validationDate <= in30).length,
    done,
    autoClosed: relevant.filter((p) => p.status === Status.FINISHED && isAutoClosed(p)).length,
    unassigned: open.filter((p) => (p.assignedUserIds?.length ?? 0) === 0).length,
    completionRate: relevant.length > 0 ? (done / relevant.length) * 100 : undefined,
  };
}

const PRIORITY_RANK: Record<string, number> = {
  [ActionPriority.HIGH]: 0,
  [ActionPriority.MEDIUM]: 1,
  [ActionPriority.LOW]: 2,
};

export const PRIORITY_TONE: Record<string, Tone> = {
  [ActionPriority.HIGH]: 'critical',
  [ActionPriority.MEDIUM]: 'warning',
  [ActionPriority.LOW]: 'neutral',
};

/**
 * The Detailmasken's default order: open before closed, overdue first, then by
 * priority, then by due date (undated last).
 */
export function byUrgency(points: ActionPointDto[] | undefined, today = new Date()): ActionPointDto[] {
  const todayIso = isoDay(today);
  const rank = (p: ActionPointDto) => {
    const state = actionState(p, todayIso);
    return state === 'overdue' ? 0 : state === 'done' || state === 'cancelled' ? 2 : 1;
  };
  return [...(points ?? [])].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (PRIORITY_RANK[a.priority ?? ''] ?? 3) - (PRIORITY_RANK[b.priority ?? ''] ?? 3) ||
      (a.validationDate ?? '9999').localeCompare(b.validationDate ?? '9999'),
  );
}

const AREA_BY_ACTION_AREA: Record<string, Area> = {
  [ActionArea.EXECUTION]: 'execution',
  [ActionArea.READINESS]: 'readiness',
  [ActionArea.QUALITY]: 'quality',
  [ActionArea.DEPENDENCIES]: 'dependencies',
};

export function actionArea(point: ActionPointDto): Area | undefined {
  return point.area ? AREA_BY_ACTION_AREA[point.area] : undefined;
}

export const isOpenAction = (point: ActionPointDto) => !isClosed(point);

/** Open action points of one area - the "N open actions relating to …" of every analysis page. */
export function openActionsInArea(points: ActionPointDto[] | undefined, area: Area): ActionPointDto[] {
  return (points ?? []).filter((p) => isOpenAction(p) && actionArea(p) === area);
}

export const isRuleAction = (point: ActionPointDto) => point.source === ActionSource.RULE;

export const isAutoClosed = (point: ActionPointDto) => point.closedReason === ActionClosedReason.RULE_RESOLVED;

/**
 * An action point's title in the viewer's language. One raised by a rule is
 * stored with the rule's English label; its translation is looked up by rule
 * key, with the phase named the way the rest of the app names it.
 */
export function actionTitle(t: TFunction, point: ActionPointDto, phaseNames?: Map<number, string>): string {
  if (!isRuleAction(point) || !point.ruleKey) return point.name ?? `#${point.id}`;
  const label = t(`templates:report.rules.${point.ruleKey.replace(/\./g, '_')}`, {
    defaultValue: point.name ?? point.ruleKey,
  });
  const scope =
    point.ruleScopeType === KpiScope.PHASE && point.ruleScopeId != null
      ? phaseNames?.get(point.ruleScopeId)
      : undefined;
  return scope ? `${label} – ${scope}` : label;
}

/** One calendar week of "Actions im Zeitverlauf". */
export interface ActionWeek {
  /** ISO calendar week number. */
  week: number;
  /** Monday and Sunday of the week, "YYYY-MM-DD". */
  start: string;
  end: string;
  raised: number;
  resolved: number;
}

export interface ActionFlow {
  weeks: ActionWeek[];
  raised: number;
  resolved: number;
  /** The same totals over the period just before, for the "vs. previous period" change. */
  previousRaised: number;
  previousResolved: number;
}

/** ISO 8601 week number of a date (weeks start on Monday, week 1 holds the first Thursday). */
export function isoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/**
 * "Actions im Zeitverlauf": per calendar week, how many actions were raised
 * and how many were closed - by a person or automatically. Weeks start on
 * Monday; the last one is the current week. The totals of the period before
 * come along so the tiles can show the change.
 */
export function actionsOverTime(points: ActionPointDto[] | undefined, weeks = 12, today = new Date()): ActionFlow {
  const monday = new Date(today);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const mondays = Array.from({ length: weeks * 2 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() - (weeks * 2 - 1 - i) * 7);
    return d;
  });
  const starts = mondays.map(isoDay);
  const weekOf = (iso?: string | null) => {
    if (!iso) return -1;
    const day = iso.slice(0, 10);
    let index = -1;
    starts.forEach((start, i) => {
      if (day >= start) index = i;
    });
    return index;
  };
  const rows: ActionWeek[] = mondays.map((d, i) => {
    const sunday = new Date(d);
    sunday.setDate(sunday.getDate() + 6);
    return { week: isoWeek(d), start: starts[i], end: isoDay(sunday), raised: 0, resolved: 0 };
  });
  for (const point of points ?? []) {
    const raisedIn = weekOf(point.created);
    if (raisedIn >= 0) rows[raisedIn].raised++;
    const closedIn = weekOf(point.closedAt);
    if (closedIn >= 0) rows[closedIn].resolved++;
  }
  const previous = rows.slice(0, weeks);
  const current = rows.slice(weeks);
  const sum = (list: ActionWeek[], key: 'raised' | 'resolved') => list.reduce((total, w) => total + w[key], 0);
  return {
    weeks: current,
    raised: sum(current, 'raised'),
    resolved: sum(current, 'resolved'),
    previousRaised: sum(previous, 'raised'),
    previousResolved: sum(previous, 'resolved'),
  };
}
